import React, { useState } from "react"
import { Upload, message, Button, List } from "antd"
import {
  UploadOutlined,
  CheckCircleOutlined,
  DeleteOutlined,
} from "@ant-design/icons"
import { translations } from "../../utils/Translation/staticTranslation"

const { Dragger } = Upload

const CustomUpload = ({
  onChange,
  acceptedFormats,
  maxSizeMB,
  uploadText,
  form,
  name,
  bulkUpload,
}) => {
  const lang = localStorage.getItem("language") || "ar"
  const upload_file_comp = translations[lang]?.upload_file_comp

  const isImageUpload = acceptedFormats.some((format) =>
    [".jpg", ".jpeg", ".png", ".gif", ".webp", ".xls", ".xlsx"].includes(
      format.toLowerCase()
    )
  )
  const allowMultiple = bulkUpload && isImageUpload

  const [file, setFile] = useState(null)
  const [fileList, setFileList] = useState([])
  const [uploadSuccess, setUploadSuccess] = useState(false)
  const [shownSizeErrorFiles, setShownSizeErrorFiles] = useState(new Set())

  // ---------- Single Upload ----------
  const handleSingleUpload = (uploadedFile) => {
    const isValidType = acceptedFormats.some((format) =>
      uploadedFile.name.toLowerCase().endsWith(format)
    )
    const isValidSize = uploadedFile.size / 1024 / 1024 < maxSizeMB

    if (!isValidType) {
      message.error(
        `${upload_file_comp?.only} ${acceptedFormats.join(", ")} ${
          upload_file_comp?.file_allowed
        }`
      )
      return false
    }

    if (!isValidSize) {
      if (!shownSizeErrorFiles.has(uploadedFile.name)) {
        message.error(`${upload_file_comp?.file_size_error} ${maxSizeMB}MB.`)
        setShownSizeErrorFiles((prev) => new Set(prev).add(uploadedFile.name))
      }
      return false
    }

    setFile(uploadedFile)
    onChange(uploadedFile)
    message.success(upload_file_comp?.file_add_success_msg)
    setUploadSuccess(true)

    return false
  }

  // ---------- Multiple Upload ----------
  const handleMultipleUpload = (info) => {
    const newFiles = [...info.fileList]
      .filter(
        (file) =>
          !fileList.some(
            (existingFile) =>
              existingFile.uid === file.uid || existingFile.name === file.name
          )
      )
      .map((file) => {
        const isValidType = acceptedFormats.some((format) =>
          file.name.toLowerCase().endsWith(format)
        )
        const isValidSize = file.size / 1024 / 1024 < maxSizeMB

        if (!isValidType) {
          message.error(
            `${upload_file_comp?.file} ${file.name}: ${
              upload_file_comp?.only
            } ${acceptedFormats.join(", ")} ${upload_file_comp?.file_allowed}.`
          )
          return null
        }

        if (!isValidSize) {
          if (!shownSizeErrorFiles.has(file.name)) {
            message.error(
              `${upload_file_comp?.file} ${file.name}: ${upload_file_comp?.file_size_error} ${maxSizeMB}MB.`
            )
            setShownSizeErrorFiles((prev) => new Set(prev).add(file.name))
          }
          return null
        }

        return file.originFileObj
      })
      .filter(Boolean)

    if (newFiles.length > 0) {
      const updatedFileList = [...fileList, ...newFiles]
      setFileList(updatedFileList)
      onChange(updatedFileList)
      message.success(
        `${newFiles.length} ${upload_file_comp?.file_add_success_msg}`
      )
      setUploadSuccess(true)
    }
  }

  // ---------- Upload Props ----------
  const uploadProps = allowMultiple
    ? {
        name: "file",
        multiple: true,
        showUploadList: false,
        accept: acceptedFormats.join(","),
        beforeUpload: () => false,
        onChange: handleMultipleUpload,
      }
    : {
        name: "file",
        multiple: false,
        showUploadList: false,
        accept: acceptedFormats.join(","),
        maxCount: 1,
        beforeUpload: (file) => handleSingleUpload(file),
      }

  // ---------- Error Border ----------
  const fieldError =
    form && name ? form.getFieldError(name) : form && form.getFieldError("logo")
  const hasError = fieldError && fieldError.length > 0

  // ---------- Remove Files ----------
  const removeFile = (index) => {
    const updatedFileList = [...fileList]
    updatedFileList.splice(index, 1)
    setFileList(updatedFileList)
    onChange(updatedFileList)

    if (updatedFileList.length === 0) {
      setUploadSuccess(false)
      if (form && name) {
        form.setFields([{ name: name, value: null }])
      }
    }
  }

  const removeAllFiles = () => {
    setFileList([])
    setUploadSuccess(false)
    if (form && name) {
      form.setFields([{ name: name, value: null }])
    }
    onChange([])
  }

  const removeSingleFile = () => {
    setFile(null)
    setUploadSuccess(false)
    if (form && name) {
      form.setFields([{ name: name, value: null }])
    }
    onChange(null)
  }

  // ---------- Render ----------
  return (
    <div style={{ width: "100%" }}>
      <Dragger
        {...uploadProps}
        style={{
          border: uploadSuccess
            ? "2px dashed #52c41a"
            : hasError
            ? "2px dashed red"
            : "2px dashed #D0D0D0",
          borderRadius: "8px",
          padding: "20px",
          background: "transparent",
        }}
      >
        <div style={{ textAlign: "center" }}>
          {uploadSuccess ? (
            <CheckCircleOutlined
              style={{
                fontSize: "24px",
                marginBottom: "10px",
                color: "#52c41a",
              }}
            />
          ) : (
            <UploadOutlined
              style={{
                fontSize: "24px",
                marginBottom: "10px",
                color: hasError ? "red" : "#999",
              }}
            />
          )}

          <p
            className={`text-14 ${
              uploadSuccess
                ? "text-green-500"
                : hasError
                ? "text-red-500"
                : "text-FD853A"
            }`}
          >
            {uploadSuccess
              ? allowMultiple
                ? `${fileList.length} ${upload_file_comp?.file_uploaded}`
                : upload_file_comp?.file_added
              : upload_file_comp?.click_to_upload}
            {!uploadSuccess && (
              <span className="text-3F3F3F font-400">
                {" "}
                {upload_file_comp?.or_drag_and_drop}
              </span>
            )}
          </p>

          <p
            className={`text-12 font-400 ${
              uploadSuccess
                ? "text-green-500"
                : hasError
                ? "text-red-500"
                : "text-3F3F3F"
            }`}
          >
            {uploadSuccess
              ? allowMultiple
                ? upload_file_comp?.can_upload_more_files
                : file?.name
              : uploadText}
          </p>
        </div>
      </Dragger>

      {allowMultiple && fileList.length > 0 && (
        <div style={{ marginTop: "10px" }}>
          <List
            size="small"
            bordered
            style={{ borderRadius: "8px", marginBottom: "10px" }}
            dataSource={fileList}
            renderItem={(file, index) => (
              <List.Item
                actions={[
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => removeFile(index)}
                  />,
                ]}
              >
                <div style={{ display: "flex", alignItems: "center" }}>
                  <CheckCircleOutlined
                    style={{ color: "#52c41a", marginRight: "8px" }}
                  />
                  {file.name}
                </div>
              </List.Item>
            )}
          />

          {fileList.length > 1 && (
            <div className="d-flex justify-content-center">
              <Button type="text" danger onClick={removeAllFiles}>
                {upload_file_comp?.remove_all_files}
              </Button>
            </div>
          )}
        </div>
      )}

      {!allowMultiple && uploadSuccess && (
        <div
          className="d-flex justify-content-center"
          style={{ marginTop: "10px" }}
        >
          <Button type="text" danger onClick={removeSingleFile}>
            {upload_file_comp?.remove_file}
          </Button>
        </div>
      )}
    </div>
  )
}

export default CustomUpload
